import { Trans } from "@lingui/macro";
import { IApiEpBillingBill, IApiEpBillingPaymentProvider } from "@smp/api";
import { RefObject, useCallback, useEffect, useMemo, useState } from "react";

import { useCloudpayments } from "@/hooks/use_cloudpayments";
import { gscSendUserParams } from "@/utils";

export const Single = ({
  bill,
  onClose,
  onReachGoal,
  description = "",
  provider,
  refDialog,
}: {
  bill: IApiEpBillingBill;
  onClose: (fireSuccessAction?: boolean) => void;
  onReachGoal: () => void;
  provider: IApiEpBillingPaymentProvider;
  description?: string;
  refDialog?: RefObject<HTMLDivElement>;
}) => {
  const { isLoaded, pay } = useCloudpayments();

  const [errorMessage, setErrorMessage] = useState<string | undefined>(
    undefined,
  );

  const currencySupported = provider.currencies_supported[bill.currency];

  const node = refDialog?.current;

  const tabIndexesAndElements = useMemo<Array<[Element, string | null]>>(
    () =>
      node
        ? Array.from(node.querySelectorAll("[role] [tabindex]")).map((el) => [
            el,
            el.getAttribute("tabindex"),
          ])
        : [],
    [node],
  );

  const removeTabIndexes = useCallback(() => {
    tabIndexesAndElements.forEach(([node]) => node.removeAttribute("tabindex"));
  }, [tabIndexesAndElements]);

  const backTabIndexes = useCallback(() => {
    tabIndexesAndElements.forEach(
      ([node, tabIndex]) => tabIndex && node.setAttribute("tabindex", tabIndex),
    );
  }, [tabIndexesAndElements]);

  useEffect(() => {
    if (!isLoaded || !currencySupported) {
      return;
    }

    removeTabIndexes();

    onReachGoal();

    pay(
      /**
       * According to https://developers.cloudpayments.ru/en/#widget-installation
       * "charge" should be used for single confirmation payment scheme
       */
      "charge",
      {
        amount: Number(bill.amount),
        currency: bill.currency,
        data: {},
        description,
        invoiceId: String(bill.id),
        publicId: currencySupported.public_id,
        skin: "modern",
      },
      {
        onComplete: () => {
          // WARNING: onComplete fired when user cant pass not robot verification
          // ensure we dont need to tab indexes on some other fires
        },
        onFail: (errorMessage) => {
          backTabIndexes();
          setErrorMessage(errorMessage);
          gscSendUserParams({ is_payment_canceled: true });
        },
        onSuccess: () => {
          backTabIndexes();
          onClose(true);
        },
      },
    );
  }, [
    backTabIndexes,
    bill.amount,
    bill.currency,
    bill.id,
    currencySupported,
    description,
    isLoaded,
    onClose,
    onReachGoal,
    pay,
    removeTabIndexes,
  ]);

  if (errorMessage) {
    return <Trans>It looks like you canceled the payment!</Trans>;
  }

  return <Trans>Payment in progress, do not close this window...</Trans>;
};
